import React from 'react'
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  MultiTableConfigTable,
} from '../../types/gotypes'
import { normalizeTablePath, arePathsEqual } from '../../utils/multitables'

interface MultiTableTabsProps {
  tables: MultiTableConfigTable[]
  selectedTablePath: string
  onTableSelect: (tablePath: string) => void
  onRemoveTable?: (index: number) => void
}

const MultiTableTabs: React.FC<MultiTableTabsProps> = ({
  tables,
  selectedTablePath,
  onTableSelect,
  onRemoveTable,
}) => {
  // Helper function to get full path from table config
  const getFullPath = (table: MultiTableConfigTable): string => {
    return table.folderPath ? `${table.folderPath}/${table.filename}` : table.filename
  }

  // Determine current selected tab index using normalized path comparison
  const currentTabIndex = tables.findIndex(table => {
    const tablePath = getFullPath(table)
    const isMatch = arePathsEqual(tablePath, selectedTablePath)

    // Debug logging in development mode
    if (process.env.NODE_ENV === 'development' && !isMatch) {
      console.debug('[MultiTableTabs] Path comparison:', {
        tablePath,
        selectedTablePath,
        normalized1: normalizeTablePath(tablePath, true),
        normalized2: normalizeTablePath(selectedTablePath, true),
      })
    }

    return isMatch
  })

  const selectedIndex = currentTabIndex >= 0 ? currentTabIndex : 0

  // Log warning if no match found (should help catch future issues)
  if (currentTabIndex < 0 && selectedTablePath && process.env.NODE_ENV === 'development') {
    console.warn('[MultiTableTabs] No matching tab found for selectedTablePath:', selectedTablePath, 'Available tables:', tables.map(getFullPath))
  }

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    if (newIndex < tables.length) {
      const selectedTable = tables[newIndex]
      if (selectedTable) {
        const fullPath = getFullPath(selectedTable)
        // Normalize before passing to parent (remove .cbt for consistency with URL params)
        const normalizedPath = normalizeTablePath(fullPath, true)
        onTableSelect(normalizedPath)
      }
    }
  }

  // Handle remove table
  const handleRemoveTable = (index: number, event: React.MouseEvent) => {
    event.stopPropagation()
    if (onRemoveTable) {
      onRemoveTable(index)
    }
  }

  if (tables.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ color: 'text.secondary' }}>
          No tables selected
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      borderBottom: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper'
    }}>
      <Tabs
        value={selectedIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flex: 1, minHeight: 48 }}
      >
        {tables.map((table, index) => (
          <Tab
            key={`${getFullPath(table)}-${index}`}
            label={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                maxWidth: 200
              }}>
                <Box sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {table.displayName}
                </Box>
                {onRemoveTable && (
                  <IconButton
                    size="small"
                    component="div"
                    onClick={(e) => handleRemoveTable(index, e)}
                    sx={{ 
                      ml: 1, 
                      p: 0.25,
                      '&:hover': { 
                        bgcolor: 'error.light',
                        color: 'error.contrastText'
                      }
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            }
            sx={{
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem'
            }}
          />
        ))}
      </Tabs>
    </Box>
  )
}

export default MultiTableTabs
